/* eslint-disable @typescript-eslint/no-explicit-any */
// composant pas utilisé
import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { StructureService } from '@app/shared/structure/structure.service';
import { MaquetteStructure, MaquettesService, ObjetMaquetteDetail, ObjetMaquetteStructure } from '@pcscol/odf-api-v1-angular';
import { ClusterNode, Edge, Layout, Node, Orientation, GraphModule } from '@swimlane/ngx-graph';
import * as shape from 'd3-shape';
import { Observable, Subject, of } from 'rxjs';
import { distinctUntilChanged, map, switchMap, takeUntil, tap, withLatestFrom } from 'rxjs/operators';
import { TreeViewFiltres, TreeViewService } from '../../services/tree-view.service';
import { CustomPegaseLayout } from './customPegaseLayout';
import { ActivatedRoute } from '@angular/router';
import { NgClass, NgStyle, AsyncPipe } from '@angular/common';
import { MatCard, MatCardTitle } from '@angular/material/card';
import { MatIcon } from '@angular/material/icon';
import { MatTooltip } from '@angular/material/tooltip';
import { MatMiniFabButton } from '@angular/material/button';

@Component({
    selector: 'app-graph',
    templateUrl: './graph.component.html',
    styleUrls: ['./graph.component.scss'],
    imports: [GraphModule, NgClass, MatCard, NgStyle, MatCardTitle, MatIcon, MatTooltip, MatMiniFabButton, AsyncPipe]
})
export class GraphComponent implements OnInit, OnDestroy {
    private maquettesService = inject(MaquettesService);
    private structureService = inject(StructureService);
    private treeView = inject(TreeViewService);
    private route = inject(ActivatedRoute);

    racineId = this.treeView.racineMaquette$.getValue()?.id;
    selection?: Node;
    public curve: any = shape.curveLinear;
    public nodes: Node[] = [];
    public links: Edge[] = [];
    public clusters: ClusterNode[] = [];
    public layoutClusters: Layout = new CustomPegaseLayout();
    private filtresPartiesArbre$: Observable<TreeViewFiltres> = this.treeView.filtresPartiesArbre$;
    private destroyed$ = new Subject<void>();
    public updateGraph$ = new Subject<void>();
    autoZoom = true;
    autoCenter = true;
    layoutSettings = {
        orientation: Orientation.TOP_TO_BOTTOM,
        marginX: 10,
        marginY: 10,
        edgePadding: 10,
        nodePadding: 50
    };

    graphOrientation$ = this.treeView.graphOrientation$.pipe(
        tap((orientation: Orientation) => {
            this.autoCenter = true;
            this.layoutSettings.orientation = orientation;
            this.updateGraph$.next();
        })
    );

    ngOnInit(): void {
        this.route.paramMap
            .pipe(
                map(params => params.get('id') ?? null),
                distinctUntilChanged(),
                tap((id: string | null) => {
                    this.treeView.racineMaquetteId = id;
                }),
                takeUntil(this.destroyed$)
            )
            .subscribe();
        this.treeView.racineMaquette$
            .pipe(
                takeUntil(this.destroyed$),
                tap(() => {
                    this.clearGraph();
                }),
                switchMap((r: ObjetMaquetteDetail | null) => (r ? this.loadData(r.id!) : of()))
            )
            .subscribe(() => {
                setTimeout(() => {
                    this.autoZoom = false;
                    this.autoCenter = false;
                    this.updateGraph$.next();
                }, 50);
            });
    }

    ngOnDestroy(): void {
        this.destroyed$.complete();
    }
    private loadData(id: string): Observable<MaquetteStructure> {
        return this.maquettesService.lireStructureMaquette(this.structureService.current$.value, id).pipe(
            withLatestFrom(this.filtresPartiesArbre$),
            tap(([maquette]) => {
                this.addNodes(maquette.racine);
            }),
            switchMap(([a]) => of(a))
        );
    }

    private clearGraph() {
        this.nodes = [];
        this.links = [];
        this.clusters = [];
    }

    public getStyles(node: any): any {
        if (!node.data.type) {
            return { 'background-color': '#dedede' };
        }
        if (node.data.type === 'F') {
            return { 'background-color': '#00b3de' };
        }
        if (node.data.type === 'G') {
            return {
                'background-color': '#7356ed',
                'color': '#fff'
            };
        }
        return {
            'background-color': '#ffb533'
        };
    }

    private addNodes(racine: ObjetMaquetteStructure, parentPath: string = '') {
        const node: Node = {
            id: 'ID' + parentPath + racine.id,
            label: racine.libelle,
            data: {
                type: racine.classe,
                hasChildren: racine.enfants?.length > 0,
                objetMaquette: racine
            },
            hidden: false
        };
        this.nodes.push(node);
        if (racine.classe === 'G') {
            this.clusters.push({
                id: 'CL' + parentPath + racine.id,
                childNodeIds: [
                    'ID' + parentPath + racine.id,
                    ...racine.enfants.map(e => 'ID' + parentPath + racine.id + e.objetMaquette.id)
                ],
                data: { parentId: 'ID' + parentPath + racine.id },
                layoutOptions: {},
                label: racine.libelle
            });
        }
        racine.enfants.forEach(e => {
            let strokeDasharray = '8 12';
            if (racine.classe === 'G') {
                strokeDasharray = '0 20';
            } else if (e.obligatoire) {
                strokeDasharray = '';
            }
            const edge: Edge = {
                id: 'ID' + parentPath + e.objetMaquette.id,
                source: 'ID' + parentPath + racine.id,
                target: 'ID' + parentPath + racine.id + e.objetMaquette.id,
                data: {
                    type: e.objetMaquette.classe,
                    obligatoire: e.obligatoire,
                    hidden: false,
                    strokeDasharray,
                    enfantDeGroupement: racine.classe === 'G'
                }
            };
            this.links.push(edge);
            this.addNodes(e.objetMaquette, parentPath + racine.id);
        });
    }

    onNodeClick(event) {
        this.selection = event;
        this.treeView.selectObjetMaquette(event.data.objetMaquette);
    }

    toggleEnfants(node) {
        node.enfantsCaches = !node.enfantsCaches;
        this.recursiveToggleEnfants(node);
        this.updateGraph$.next();
    }

    private recursiveToggleEnfants(node: Node, show?: boolean) {
        this.links
            .filter(l => l.source === node.id)
            .forEach(l => {
                this.nodes.filter(n => n.id === l.target).forEach(n => (n.hidden = show ?? !n.hidden));
                l.data.hidden = show ?? !l.data.hidden;
                const child = this.nodes.find(n => n.id === l.target);
                this.recursiveToggleEnfants(child!, l.data.hidden);
            });
        this.clusters.filter(c => c.data.parentId === node.id).forEach(c => (c.data.hidden = show ?? !c.data.hidden));
    }
}
